import type { DocSummary } from "@xop/dashboard-shared";

/**
 * Pure tree + URL logic for the documentation sidebar, extracted from
 * `DocumentationSidebar.tsx` so it unit-tests directly.
 *
 * THE DOCUMENTATION IS THE LIBRARY SHELF (2026-09-14). A document pinned
 * to a release (`default_release`) is release documentation: it is read
 * under its version on CRM ▸ Releases and is NOT listed here — the tree
 * used to carry a second, Releases root that duplicated that page one
 * click away, and the duplicate was what made the section hard to read.
 * Everything else — the library — buckets by folder, and only those
 * unversioned documents are counted on a folder.
 */

export interface CategoryNode {
  /** Real category id, or `null` for the synthetic "Uncategorized"
   * bucket. */
  id: number | null;
  slug: string;
  name: string;
  /** LIBRARY documents in this folder — release documentation is read
   * under its version, never counted here. */
  count: number;
  /** `visible_in_customer_portal === false` — dimmed in the tree so staff
   * see at a glance which folders customers cannot. */
  hiddenInPortal: boolean;
  /** `visible_in_reseller_portal === true` — sharing with resellers is the
   * exception, so the tree surfaces the POSITIVE state. */
  visibleToResellers: boolean;
  /** Sub-folders — TWO levels below the top (2026-10-31): "Videos" ▸
   * "R11.0" ▸ "Command Control". portal-api refuses a fourth tier. */
  children: CategoryNode[];
}

export interface DocTree {
  library: CategoryNode[];
}

export interface CategoryLike {
  id: number;
  name: string;
  slug: string;
  icon?: string | null;
  visible_in_customer_portal?: boolean;
  visible_in_reseller_portal?: boolean;
  parent_id?: number | null;
}

/** Read the active folder slug and library doc slug from the URL itself
 * rather than `useParams()` — the sidebar is rendered OUTSIDE the Outlet
 * (inside `DocumentationLayout`), so useParams resolves against the
 * layout's route, not the child route, and the named params would always
 * come back undefined.
 *
 * A release page (`/documentation/release/:id`) and the legacy folder +
 * release path still parse so the reader's folder can be highlighted when
 * a release document opens; neither shape is handed out by the tree. */
export function pickActiveSelection(pathname: string): {
  categorySlug: string | null;
  releaseId: string | null;
  libraryDocSlug: string | null;
} {
  const libMatch = pathname.match(/\/documentation\/library\/([^/]+)/);
  if (libMatch) {
    return {
      categorySlug: null,
      releaseId: null,
      libraryDocSlug: decodeURIComponent(libMatch[1] ?? ""),
    };
  }
  const releaseMatch = pathname.match(/\/documentation\/release\/([^/]+)/);
  if (releaseMatch) {
    return {
      categorySlug: null,
      releaseId: decodeURIComponent(releaseMatch[1] ?? ""),
      libraryDocSlug: null,
    };
  }
  const typeMatch = pathname.match(/\/documentation\/type\/([^/]+)(?:\/release\/([^/]+))?/);
  if (typeMatch) {
    return {
      categorySlug: decodeURIComponent(typeMatch[1] ?? ""),
      releaseId: typeMatch[2] ? decodeURIComponent(typeMatch[2]) : null,
      libraryDocSlug: null,
    };
  }
  return { categorySlug: null, releaseId: null, libraryDocSlug: null };
}

/** Walk the doc list once and bucket the LIBRARY by folder.
 *
 * Folders sort by count desc, then name — the busiest folder is the one an
 * operator most often wants, and it is also where `/documentation` lands.
 * The "Uncategorized" bucket picks up every library doc whose `category`
 * is null, appended last and only when it has docs. */
export function buildTree(cats: CategoryLike[], docs: DocSummary[]): DocTree {
  const byCat = new Map<string, CategoryNode>();
  for (const c of cats) {
    byCat.set(c.slug, {
      id: c.id,
      slug: c.slug,
      name: c.name,
      count: 0,
      hiddenInPortal: c.visible_in_customer_portal === false,
      visibleToResellers: c.visible_in_reseller_portal === true,
      children: [],
    });
  }
  const uncategorized: CategoryNode = {
    id: null,
    slug: "uncategorized",
    name: "Uncategorized",
    count: 0,
    hiddenInPortal: false,
    visibleToResellers: false,
    children: [],
  };

  for (const doc of docs) {
    if (doc.default_release) continue; // release documentation — read under its version
    const node = doc.category ? byCat.get(doc.category.slug) : uncategorized;
    if (!node) continue;
    node.count += 1;
  }

  // Nest each sub-folder under its parent. A row whose parent is missing
  // (deleted, or not in this list) is shown at the top level rather than lost.
  const byId = new Map<number, CategoryNode>();
  for (const node of byCat.values()) if (node.id != null) byId.set(node.id, node);
  const roots: CategoryNode[] = [];
  for (const c of cats) {
    const node = byCat.get(c.slug);
    if (!node) continue;
    const parent = c.parent_id != null ? byId.get(c.parent_id) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  // Sub-folders sort by name at every level; walking with a seen set keeps a
  // legacy cycle from recursing forever.
  const sortChildren = (n: CategoryNode, seen: Set<CategoryNode>): void => {
    if (seen.has(n)) return;
    seen.add(n);
    n.children.sort((a, b) => a.name.localeCompare(b.name));
    for (const ch of n.children) sortChildren(ch, seen);
  };
  const sorted = new Set<CategoryNode>();
  for (const r of roots) sortChildren(r, sorted);

  // A folder's weight includes everything below it, so a parent that only
  // holds sub-folders still sorts (and lands) where its documents are.
  const ordered = Array.from(new Set(roots)).sort((a, b) => {
    const wa = totalCount(a);
    const wb = totalCount(b);
    if (wa !== wb) return wb - wa;
    return a.name.localeCompare(b.name);
  });
  if (uncategorized.count > 0) ordered.push(uncategorized);
  return { library: ordered };
}

/** Documents in a folder AND every sub-folder below it — what a folder row
 * on a parent's page promises it holds. */
export function totalCount(node: CategoryNode, seen: Set<CategoryNode> = new Set()): number {
  if (seen.has(node)) return 0;
  seen.add(node);
  return node.children.reduce((sum, ch) => sum + totalCount(ch, seen), node.count);
}
